import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { portfolioApi, likesApi } from '../services/api';
import { Input } from '@/components/ui/input';
import {
  X, Heart, MapPin, Clock, ArrowLeft, Sparkles, Search,
  Mail, Linkedin, Github, Twitter, Globe, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const THEME_STYLES = {
  minimal: {
    bg: 'bg-white',
    header: 'bg-gradient-to-br from-orange-50 to-white',
    accent: 'text-[#e8744f]',
    badge: 'bg-orange-50 text-gray-700 border border-orange-100',
    prompt: 'bg-gray-50 border border-gray-100',
    text: 'text-gray-500',
    name: 'text-gray-900',
    section: 'text-[#e8744f]',
  },
  dark: {
    bg: 'bg-gray-900',
    header: 'bg-gradient-to-br from-gray-800 to-gray-900',
    accent: 'text-orange-400',
    badge: 'bg-gray-700 text-gray-200 border border-gray-600',
    prompt: 'bg-gray-800 border border-gray-700',
    text: 'text-gray-400',
    name: 'text-white',
    section: 'text-orange-400',
  },
  creative: {
    bg: 'bg-orange-50',
    header: 'bg-gradient-to-br from-[#e8744f] to-[#d66440]',
    accent: 'text-[#e8744f]',
    badge: 'bg-orange-100 text-orange-800 border border-orange-200',
    prompt: 'bg-white border border-orange-100',
    text: 'text-orange-900/60',
    name: 'text-white',
    section: 'text-[#e8744f]',
  },
};

export default function Browse() {
  const [portfolios, setPortfolios] = useState([]);
  const [allPortfolios, setAllPortfolios] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showMatch, setShowMatch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const loadPortfolios = useCallback(async () => {
    // Reset everything on every fresh navigation
    setCurrentIndex(0);
    setLoading(true);
    setSearchQuery('');
    setSearchMode(false);
    setPortfolios([]);
    setAllPortfolios([]);

    try {
      const { data } = await portfolioApi.search(0, 500);
      setPortfolios(data);
      setAllPortfolios(data);

      // If coming from FindPeople or Admin with a specific portfolio, jump to it
      const { startPortfolioId } = location.state || {};
      if (startPortfolioId != null && data.length > 0) {
        const idx = data.findIndex(p => p.id === startPortfolioId);
        if (idx >= 0) setCurrentIndex(idx);
      }
    } catch (error) {
      console.error('Failed to load portfolios:', error);
      toast.error('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  // location.key is unique per navigation — guarantees fresh load every time Browse is visited
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPortfolios(); }, [loadPortfolios]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setPortfolios(allPortfolios);
      setSearchMode(false);
      setCurrentIndex(0);
    } else {
      setSearchMode(true);
      const filtered = allPortfolios.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.title.toLowerCase().includes(query.toLowerCase())
      );
      setPortfolios(filtered);
      setCurrentIndex(0);
    }
  };

  const handleAction = async (action) => {
    if (currentIndex >= portfolios.length) return;
    const portfolio = portfolios[currentIndex];
    try {
      const { data } = await likesApi.like(portfolio.id, action);
      if (data.matched) {
        setShowMatch(true);
        setTimeout(() => setShowMatch(false), 3000);
      }
      setCurrentIndex(prev => prev + 1);
      if (action === 'like') toast.success('Liked!');
    } catch (error) {
      console.error('Action failed:', error);
      toast.error('Action failed');
    }
  };

  /* ── Loading ── */
  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e8744f] mx-auto mb-3" />
        <p className="text-sm text-gray-400 animate-pulse">Finding amazing portfolios...</p>
      </div>
    </div>
  );

  /* ── Empty / Done ── */
  if (portfolios.length === 0 || currentIndex >= portfolios.length) return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd] flex flex-col items-center justify-center p-6 text-center">
      <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
        <Sparkles className="text-[#e8744f]" size={28} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {searchMode ? 'No portfolios found' : "You're all caught up!"}
      </h2>
      <p className="text-sm text-gray-500 mb-6 max-w-sm">
        {searchMode ? 'Try a different search term.' : "You've seen all available portfolios. Check back later!"}
      </p>
      <div className="flex gap-3">
        {searchMode && (
          <button onClick={() => handleSearch('')}
            className="px-5 py-2.5 rounded-full border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition">
            Clear Search
          </button>
        )}
        <Link to="/">
          <button className="px-5 py-2.5 rounded-full bg-[#e8744f] hover:bg-[#d66440] text-white text-sm font-medium transition shadow-md">
            Go Home
          </button>
        </Link>
      </div>
    </div>
  );

  const p = portfolios[currentIndex];
  const ts = THEME_STYLES[p.theme] || THEME_STYLES.minimal;
  const social = p.social_links || {};
  const prompts = [
    { icon: '🌟', q: 'What makes me different', val: p.uniqueness },
    { icon: '🔥', q: 'What excites me', val: p.passion },
    { icon: '🏆', q: "I'm most proud of", val: p.proudest_work },
    { icon: '💭', q: 'Dream project', val: p.dream_project },
    { icon: '☀️', q: 'A typical day', val: p.typical_day },
    { icon: '💬', q: 'Best career advice', val: p.career_advice },
  ].filter(pr => pr.val);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">

      {/* Match overlay */}
      {showMatch && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 text-center shadow-2xl max-w-sm w-full animate-bounce">
            <Heart className="mx-auto mb-4 text-red-500" size={48} fill="#ef4444" />
            <h2 className="text-2xl font-bold mb-2 text-gray-900">It's a Match! 🎉</h2>
            <p className="text-gray-500 text-sm">You both liked each other!</p>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-orange-100/60 px-4 py-3 flex items-center justify-between gap-3">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition text-sm shrink-0">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex-1 relative max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name or title..."
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            data-testid="search-input"
            className="pl-8 text-xs rounded-full border-gray-200 h-8 focus:border-[#e8744f] focus:ring-1 focus:ring-orange-100"
          />
        </div>
        <span className="text-xs text-gray-400 font-medium shrink-0">
          {currentIndex + 1} / {portfolios.length}
        </span>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* ── Portfolio Card ── */}
        <div className={`rounded-3xl overflow-hidden shadow-lg border border-gray-100 ${ts.bg}`} data-testid="portfolio-browser">

          {/* Header */}
          <div className={`p-6 pb-5 ${ts.header}`}>
            <div className="flex items-start gap-4">
              <div className={`w-20 h-20 rounded-full overflow-hidden flex-shrink-0 border-2 ${p.theme === 'creative' ? 'border-white/30' : 'border-orange-200'}`}>
                {p.avatar_url ? (
                  <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#e8744f] flex items-center justify-center text-white text-3xl font-bold">
                    {p.name?.charAt(0)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className={`text-2xl font-bold truncate ${ts.name}`} data-testid="portfolio-name">{p.name}</h2>
                <p className={`text-sm mb-2 ${p.theme === 'creative' ? 'text-white/80' : ts.text}`} data-testid="portfolio-title">{p.title}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(p.profession || p.branch) && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                      🎓 {[p.branch, p.profession].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {p.availability && (
                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                      p.availability === 'Actively looking' ? 'bg-green-500/20 text-green-600' :
                      p.availability === 'Not available' ? 'bg-red-500/20 text-red-500' :
                      'bg-orange-500/20 text-orange-600'
                    }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />{p.availability}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  {p.location && (
                    <span className={`flex items-center gap-1 ${p.theme === 'creative' ? 'text-white/70' : ts.text}`}>
                      <MapPin size={10} />{p.location}
                    </span>
                  )}
                  {p.years_experience && (
                    <span className={`flex items-center gap-1 ${p.theme === 'creative' ? 'text-white/70' : ts.text}`}>
                      <Clock size={10} />{p.years_experience}+ yrs
                    </span>
                  )}
                  {p.views != null && (
                    <span className={`flex items-center gap-1 ${p.theme === 'creative' ? 'text-white/70' : ts.text}`}>
                      <Eye size={10} />{p.views} views
                    </span>
                  )}
                </div>
              </div>
            </div>
            {p.tagline && (
              <p className={`mt-4 text-sm italic ${p.theme === 'creative' ? 'text-white/80' : ts.text}`}>
                "{p.tagline}"
              </p>
            )}
          </div>

          {/* Gallery */}
          {p.gallery?.length > 0 && (
            <div className="px-5 py-3 flex gap-2 overflow-x-auto">
              {p.gallery.map((url, i) => (
                <div key={i} className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={url} alt="gallery" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* Body */}
          <div className="p-5 space-y-5">

            {p.bio && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>About Me</h3>
                <p className={`text-sm leading-relaxed ${ts.text}`}>{p.bio}</p>
              </div>
            )}

            {p.skills?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {p.skills.map((s, i) => <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{s}</span>)}
                </div>
              </div>
            )}

            {p.tools?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Tools</h3>
                <div className="flex flex-wrap gap-2">
                  {p.tools.map((t, i) => <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{t}</span>)}
                </div>
              </div>
            )}

            {p.languages?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {p.languages.map((l, i) => <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{l}</span>)}
                </div>
              </div>
            )}

            {p.projects?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-3 ${ts.section}`}>Projects</h3>
                <div className="grid grid-cols-2 gap-3">
                  {p.projects.map((proj, i) => (
                    <div key={i} className={`rounded-2xl overflow-hidden ${ts.prompt}`}>
                      {proj.image_url && <img src={proj.image_url} alt={proj.title} className="w-full h-28 object-cover" />}
                      <div className="p-3">
                        <p className={`text-sm font-bold ${p.theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{proj.title}</p>
                        {proj.description && <p className={`text-xs mt-0.5 line-clamp-2 ${ts.text}`}>{proj.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prompts.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-3 ${ts.section}`}>Get to Know Me</h3>
                <div className="space-y-3">
                  {prompts.map((pr, i) => (
                    <div key={i} className={`rounded-2xl p-4 ${ts.prompt}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-base">{pr.icon}</span>
                        <span className={`text-xs font-bold ${ts.accent}`}>{pr.q}</span>
                      </div>
                      <p className={`text-sm leading-relaxed ${ts.text}`}>{pr.val}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(social.linkedin || social.github || social.twitter || social.portfolio_url) && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Connect</h3>
                <div className="flex flex-wrap gap-2">
                  {social.linkedin && (
                    <a href={social.linkedin.startsWith('http') ? social.linkedin : `https://${social.linkedin}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  )}
                  {social.github && (
                    <a href={social.github.startsWith('http') ? social.github : `https://${social.github}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Github size={12} /> GitHub
                    </a>
                  )}
                  {social.twitter && (
                    <a href={social.twitter.startsWith('@') ? `https://twitter.com/${social.twitter.slice(1)}` : `https://twitter.com/${social.twitter}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Twitter size={12} /> Twitter
                    </a>
                  )}
                  {social.portfolio_url && (
                    <a href={social.portfolio_url.startsWith('http') ? social.portfolio_url : `https://${social.portfolio_url}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Globe size={12} /> Website
                    </a>
                  )}
                </div>
              </div>
            )}

            {p.contact_email && (
              <div className={`flex items-center gap-2 rounded-xl p-3 ${ts.prompt}`}>
                <Mail size={14} className={ts.accent} />
                <span className={`text-sm ${ts.text}`}>{p.contact_email}</span>
              </div>
            )}

          </div>
        </div>

        {/* ── Action Buttons ── */}
        <div className="flex justify-center gap-6 py-4">
          <button
            onClick={() => handleAction('dislike')}
            data-testid="dislike-button"
            className="w-16 h-16 rounded-full border-2 border-red-200 bg-white flex items-center justify-center shadow-sm hover:bg-red-50 hover:border-red-300 hover:scale-110 active:scale-95 transition-all duration-200"
          >
            <X size={26} className="text-red-400" />
          </button>
          <button
            onClick={() => handleAction('like')}
            data-testid="like-button"
            className="w-16 h-16 rounded-full border-2 border-green-200 bg-white flex items-center justify-center shadow-sm hover:bg-green-50 hover:border-green-300 hover:scale-110 active:scale-95 transition-all duration-200"
          >
            <Heart size={26} className="text-green-400" />
          </button>
        </div>

      </div>
    </div>
  );
}